import React, { useEffect, useState } from "react";
import Navbar from "@/components/Navbar";
import { api, extractError } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, Plus, Trash2, Tag, Users } from "lucide-react";

const PLAN_OPTIONS = [
  { key: "family_plus", label: "Family Plus" },
  { key: "family_premium", label: "Family Premium" },
  { key: "sitter_enhanced", label: "Sitter Enhanced" },
];

export default function AdminPromoCodes() {
  const [codes, setCodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ code: "", plan: "family_plus", months_free: 1, max_redemptions: 100 });
  const [creating, setCreating] = useState(false);

  const [grantForm, setGrantForm] = useState({ user_id: "", plan: "family_plus", months: 1 });
  const [granting, setGranting] = useState(false);

  const load = async () => {
    setLoading(true);
    try { const { data } = await api.get("/admin/promo-codes"); setCodes(data.results); }
    catch (e) { toast.error(extractError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const create = async (e) => {
    e.preventDefault(); setCreating(true);
    try {
      await api.post("/admin/promo-codes", {
        code: form.code.trim().toUpperCase(),
        plan: form.plan,
        months_free: Number(form.months_free) || 1,
        max_redemptions: Number(form.max_redemptions) || 100,
      });
      toast.success("Promo code created");
      setForm({ code: "", plan: "family_plus", months_free: 1, max_redemptions: 100 });
      load();
    } catch (err) { toast.error(extractError(err)); }
    finally { setCreating(false); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this code?")) return;
    try { await api.delete(`/admin/promo-codes/${id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(extractError(e)); }
  };

  const grant = async (e) => {
    e.preventDefault(); setGranting(true);
    try {
      await api.post("/admin/subscriptions/grant", {
        user_id: grantForm.user_id.trim(),
        plan: grantForm.plan,
        months: Number(grantForm.months) || 1,
      });
      toast.success("Subscription granted");
      setGrantForm({ user_id: "", plan: "family_plus", months: 1 });
    } catch (err) { toast.error(extractError(err)); }
    finally { setGranting(false); }
  };

  return (
    <div className="min-h-screen bg-stone2-50" data-testid="admin-promos-page">
      <Navbar />
      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-10">
        <span className="section-eyebrow">Admin</span>
        <h1 className="mt-2 text-3xl font-heading font-bold text-sage-900 flex items-center gap-2">
          <Tag className="w-6 h-6 text-sage-600" /> Promo codes & subscription grants
        </h1>

        <div className="grid md:grid-cols-2 gap-6 mt-8">
          {/* Create promo */}
          <form onSubmit={create} className="paper-card p-6 space-y-4" data-testid="admin-promo-create-form">
            <h2 className="font-heading font-semibold text-sage-900">Create promo code</h2>
            <div>
              <label className="field-label">Code</label>
              <input required value={form.code}
                onChange={(e) => setForm((f) => ({ ...f, code: e.target.value }))}
                data-testid="admin-promo-code-input" placeholder="WELCOME1"
                className="w-full rounded-lg border border-stone2-200 bg-white px-3 py-2 text-sm font-mono uppercase" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="field-label">Plan</label>
                <select value={form.plan} onChange={(e) => setForm((f) => ({ ...f, plan: e.target.value }))}
                  data-testid="admin-promo-plan-select"
                  className="w-full rounded-lg border border-stone2-200 bg-white px-3 py-2 text-sm">
                  {PLAN_OPTIONS.map((p) => <option key={p.key} value={p.key}>{p.label}</option>)}
                </select>
              </div>
              <div>
                <label className="field-label">Months free</label>
                <input type="number" min="1" value={form.months_free}
                  onChange={(e) => setForm((f) => ({ ...f, months_free: e.target.value }))}
                  data-testid="admin-promo-months-input"
                  className="w-full rounded-lg border border-stone2-200 bg-white px-3 py-2 text-sm" />
              </div>
            </div>
            <div>
              <label className="field-label">Max redemptions</label>
              <input type="number" min="1" value={form.max_redemptions}
                onChange={(e) => setForm((f) => ({ ...f, max_redemptions: e.target.value }))}
                data-testid="admin-promo-max-input"
                className="w-full rounded-lg border border-stone2-200 bg-white px-3 py-2 text-sm" />
            </div>
            <button type="submit" disabled={creating} data-testid="admin-promo-create-submit" className="btn-primary text-sm">
              {creating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />} Create code
            </button>
          </form>

          {/* Grant */}
          <form onSubmit={grant} className="paper-card p-6 space-y-4" data-testid="admin-grant-form">
            <h2 className="font-heading font-semibold text-sage-900 flex items-center gap-2"><Users className="w-4 h-4" /> Grant subscription</h2>
            <div>
              <label className="field-label">User ID</label>
              <input required value={grantForm.user_id}
                onChange={(e) => setGrantForm((f) => ({ ...f, user_id: e.target.value }))}
                data-testid="admin-grant-user-input"
                placeholder="uuid-of-user"
                className="w-full rounded-lg border border-stone2-200 bg-white px-3 py-2 text-sm font-mono" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="field-label">Plan</label>
                <select value={grantForm.plan} onChange={(e) => setGrantForm((f) => ({ ...f, plan: e.target.value }))}
                  data-testid="admin-grant-plan-select"
                  className="w-full rounded-lg border border-stone2-200 bg-white px-3 py-2 text-sm">
                  {PLAN_OPTIONS.map((p) => <option key={p.key} value={p.key}>{p.label}</option>)}
                </select>
              </div>
              <div>
                <label className="field-label">Months</label>
                <input type="number" min="1" value={grantForm.months}
                  onChange={(e) => setGrantForm((f) => ({ ...f, months: e.target.value }))}
                  data-testid="admin-grant-months-input"
                  className="w-full rounded-lg border border-stone2-200 bg-white px-3 py-2 text-sm" />
              </div>
            </div>
            <button type="submit" disabled={granting} data-testid="admin-grant-submit" className="btn-accent text-sm">
              {granting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />} Grant subscription
            </button>
          </form>
        </div>

        <section className="mt-8 paper-card p-6">
          <h2 className="font-heading font-semibold text-sage-900 mb-3">Active promo codes</h2>
          {loading && <div className="text-stone2-500"><Loader2 className="w-4 h-4 animate-spin inline mr-2" /> Loading…</div>}
          {!loading && codes.length === 0 && <p className="text-sm text-stone2-500">No codes yet.</p>}
          <ul className="divide-y divide-stone2-200" data-testid="admin-promo-list">
            {codes.map((c) => (
              <li key={c.id} className="py-3 flex items-center justify-between" data-testid={`admin-promo-row-${c.id}`}>
                <div>
                  <div className="font-mono font-semibold text-sage-900">{c.code}</div>
                  <div className="text-xs text-stone2-500">
                    {c.plan} · {c.months_free} months · redeemed {c.redeemed_count}/{c.max_redemptions}
                  </div>
                </div>
                <button type="button" onClick={() => remove(c.id)}
                  data-testid={`admin-promo-delete-${c.id}`}
                  className="text-danger p-2 hover:bg-danger/5 rounded-lg">
                  <Trash2 className="w-4 h-4" />
                </button>
              </li>
            ))}
          </ul>
        </section>
      </div>
    </div>
  );
}
